import { useEffect, useMemo, useRef, useState } from "react";
import { Users } from "lucide-react";
import { CAPACITY, computeQueue, QUEUE_TONES } from "@/lib/opsIntel";

export const PriorityQueue = ({ scenarios, intelMap, selectedId, onSelect }) => {
  const queue = useMemo(() => computeQueue(scenarios, intelMap), [scenarios, intelMap]);
  const prevRanks = useRef({});
  const [lastMoves, setLastMoves] = useState({});
  const moves = {};
  queue.forEach((q) => {
    const p = prevRanks.current[q.id];
    if (p && p !== q.rank) moves[q.id] = p - q.rank;
  });
  useEffect(() => {
    prevRanks.current = Object.fromEntries(queue.map((q) => [q.id, q.rank]));
    if (Object.keys(moves).length) {
      setLastMoves(moves);
      const t = setTimeout(() => setLastMoves({}), 6000);
      return () => clearTimeout(t);
    }
  });

  const highCount = queue.filter((q) => q.qual.priority >= CAPACITY.highPriorityThreshold).length;
  const top = queue.slice(0, 5);

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5" data-testid="priority-queue">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="text-[10px] font-mono tracking-widest text-zinc-400 uppercase flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5 text-emerald-400" strokeWidth={1.5} />
          Live Priority Queue — who should the team work next
        </div>
        <span className="ml-auto text-[9px] font-mono text-zinc-500" data-testid="queue-capacity">
          HUMAN CAPACITY — {CAPACITY.agents} agents available · {highCount} high-priority · immediate routing #1–#{CAPACITY.agents}
        </span>
      </div>
      <div className="space-y-1.5">
        {top.map((q) => (
          <button
            key={q.id}
            data-testid={`queue-row-${q.id}`}
            onClick={() => onSelect(q.id)}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-sm border text-left transition-colors duration-200 ${
              q.id === selectedId ? "border-emerald-500/50 bg-emerald-500/5" : "border-zinc-800 bg-zinc-950/50 hover:border-zinc-700"
            }`}
          >
            <span className="font-mono text-sm font-bold text-emerald-400 w-8 shrink-0" data-testid={`queue-rank-${q.id}`}>#{q.rank}</span>
            <span className="text-xs font-semibold text-zinc-100 truncate">{q.name}</span>
            <span className="text-[10px] font-mono text-zinc-500 truncate hidden md:inline">{q.issue}</span>
            {lastMoves[q.id] > 0 && (
              <span className="text-[9px] font-mono font-semibold text-emerald-400 shrink-0 fade-slide-in" data-testid={`queue-move-${q.id}`}>↑ {lastMoves[q.id]}</span>
            )}
            {lastMoves[q.id] < 0 && (
              <span className="text-[9px] font-mono font-semibold text-red-400 shrink-0 fade-slide-in" data-testid={`queue-move-${q.id}`}>↓ {Math.abs(lastMoves[q.id])}</span>
            )}
            <span className="ml-auto flex items-center gap-2 shrink-0">
              {q.qual.window && (
                <span className={`px-1.5 py-0.5 rounded-sm border text-[8px] font-mono font-semibold ${QUEUE_TONES[q.qual.window] || QUEUE_TONES.STALE}`}>
                  {q.qual.window}
                </span>
              )}
              <span className="text-[10px] font-mono text-zinc-400">AP {q.qual.priority}</span>
            </span>
          </button>
        ))}
      </div>
      <div className="text-[8px] font-mono text-zinc-600 mt-2 tracking-wider">
        RANKED BY ACTION PRIORITY + INTENT + OPPORTUNITY WINDOW + FRESHNESS + CONTACTABILITY + ELIGIBILITY + RISK — REMAINING {queue.length - top.length} QUEUED BY ACTION PRIORITY
      </div>
    </div>
  );
};
